import type { AlertColor } from "@mui/material/Alert/Alert";
import {
  type QueryClient as TanStackQueryClient,
  useQueryClient as useTanStackQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import type QueryClient from "../api/query/queryClient";
import { patchInsuranceVerification } from "./InsuranceVerification/hooks";
import {
  insuranceVerificationNeedsPolling,
  needsPolling,
  PostedState,
} from "./posting";

const TERMINAL_SUCCESS = PostedState[PostedState.AUTO_POSTED];
const TERMINAL_FAILURE_STATES = [
  PostedState[PostedState.FAILURE],
  PostedState[PostedState.RETRY],
];

export const POSTING_DELTA_INTERVAL_MS = 5_000;
export const POSTING_DELTA_MAX_WALL_MS = 10 * 60_000;

export interface PostingStatus {
  wieldyId: string;
  postedState: string;
  postedAttempts: number;
  postedLatestErrorMessage: string | null;
  postingCompletedSteps: string[] | null;
  postingExpectedSteps: string[] | null;
  updatedAt: string;
}

export interface PostingStatusResponse {
  changes: PostingStatus[];
  cursor: string | null;
}

interface UsePostingStatusPollArgs {
  ids: string[];
  fetchDelta: (
    ids: string[],
    since: string | null,
    signal: AbortSignal,
  ) => Promise<PostingStatusResponse>;
  onTransition: (delta: PostingStatus) => void;
  onNoChanges?: () => void;
  intervalMs?: number;
  maxWallClockMs?: number;
}

/**
 * Polls a posting-status endpoint while `ids` is non-empty.
 * Cursor-driven: server returns only rows whose updated_at exceeds the last
 * cursor, so most polls return zero changes and zero re-renders.
 */
export function usePostingStatusPoll({
  ids,
  fetchDelta,
  onTransition,
  onNoChanges,
  intervalMs = POSTING_DELTA_INTERVAL_MS,
  maxWallClockMs = POSTING_DELTA_MAX_WALL_MS,
}: UsePostingStatusPollArgs): void {
  const cursorRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const hasTickedRef = useRef(false);
  const onTransitionRef = useRef(onTransition);
  onTransitionRef.current = onTransition;
  const fetchDeltaRef = useRef(fetchDelta);
  fetchDeltaRef.current = fetchDelta;
  const onNoChangesRef = useRef(onNoChanges);
  onNoChangesRef.current = onNoChanges;

  const idsKey = ids.length === 0 ? "" : ids.slice().sort().join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding ids, ids.length or dropping idsKey would change when this runs
  useEffect(() => {
    if (ids.length === 0) {
      startedAtRef.current = null;
      cursorRef.current = null;
      hasTickedRef.current = false;
      return undefined;
    }

    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }

    const controller = new AbortController();
    let interval: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const tick = async () => {
      if (inFlightRef.current) return;
      if (
        startedAtRef.current !== null &&
        Date.now() - startedAtRef.current > maxWallClockMs
      ) {
        stop();
        return;
      }
      inFlightRef.current = true;
      try {
        const response = await fetchDeltaRef.current(
          ids,
          cursorRef.current,
          controller.signal,
        );
        if (response.cursor) {
          cursorRef.current = response.cursor;
        }
        response.changes.forEach((change) => {
          onTransitionRef.current(change);
        });
        if (
          response.changes.length === 0 &&
          hasTickedRef.current &&
          ids.length > 0
        ) {
          onNoChangesRef.current?.();
        }
        hasTickedRef.current = true;
      } catch (err) {
        if (controller.signal.aborted) return;
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.warn("Posting status delta poll failed", err);
      } finally {
        inFlightRef.current = false;
      }
    };

    interval = setInterval(tick, intervalMs);
    return () => {
      stop();
      controller.abort();
    };
  }, [idsKey, intervalMs, maxWallClockMs]);
}

type Snackbar = (msg: { severity: AlertColor; message: string }) => void;

function applyClaimDelta(
  delta: PostingStatus,
  storeClaims: ClaimWithProcedureAndPatientMessage[],
  updateStore: (action: {
    type: "UPDATE_CLAIM";
    claimToUpdate: ClaimWithProcedureAndPatientMessage;
  }) => void,
  updateSnackBar: Snackbar,
): void {
  const existing = storeClaims.find((c) => c.wieldyId === delta.wieldyId);
  if (!existing) return;
  updateStore({
    type: "UPDATE_CLAIM",
    claimToUpdate: {
      ...existing,
      postedState: delta.postedState,
      postedAttempts: delta.postedAttempts,
      postedLatestErrorMessage: delta.postedLatestErrorMessage,
      postingCompletedSteps:
        delta.postingCompletedSteps as ClaimWithProcedureAndPatientMessage["postingCompletedSteps"],
      postingExpectedSteps:
        delta.postingExpectedSteps as ClaimWithProcedureAndPatientMessage["postingExpectedSteps"],
      updatedAt: delta.updatedAt,
    },
  });
  if (delta.postedState === TERMINAL_SUCCESS) {
    updateSnackBar({
      severity: "success",
      message: "Successfully posted to PMS",
    });
  } else if (TERMINAL_FAILURE_STATES.includes(delta.postedState)) {
    updateSnackBar({
      severity: "error",
      message:
        delta.postedLatestErrorMessage ??
        "There was an issue posting to PMS. Please reach out to Wieldy support.",
    });
  }
}

function applyVerificationDelta(
  delta: PostingStatus,
  verifications: InsuranceVerificationMessage[],
  rqClient: TanStackQueryClient,
  updateSnackBar: Snackbar,
): void {
  if (!verifications.some((v) => v.wieldyId === delta.wieldyId)) return;
  patchInsuranceVerification(rqClient, delta.wieldyId, (existing) => ({
    ...existing,
    postedState: delta.postedState,
    postedAttempts: delta.postedAttempts,
    postedLatestErrorMessage: delta.postedLatestErrorMessage,
    postingCompletedSteps:
      delta.postingCompletedSteps as InsuranceVerificationMessage["postingCompletedSteps"],
    postingExpectedSteps:
      delta.postingExpectedSteps as InsuranceVerificationMessage["postingExpectedSteps"],
    updatedAt: delta.updatedAt,
  }));
  if (delta.postedState === TERMINAL_SUCCESS) {
    updateSnackBar({
      severity: "success",
      message: "Successfully posted insurance verification to PMS",
    });
  } else if (TERMINAL_FAILURE_STATES.includes(delta.postedState)) {
    updateSnackBar({
      severity: "error",
      message:
        delta.postedLatestErrorMessage ??
        "There was an issue posting insurance verification to PMS. Please reach out to Wieldy support.",
    });
  }
}

export function useClaimsPostingPoll(
  storeClaims: ClaimWithProcedureAndPatientMessage[],
  queryClient: QueryClient,
  updateStore: (action: {
    type: "UPDATE_CLAIM";
    claimToUpdate: ClaimWithProcedureAndPatientMessage;
  }) => void,
  updateSnackBar: Snackbar,
): void {
  const ids = useMemo(
    () => storeClaims.filter(needsPolling).map((c) => c.wieldyId),
    [storeClaims],
  );
  usePostingStatusPoll({
    ids,
    fetchDelta: (xs, since, signal) =>
      queryClient.fetchClaimsPostingStatus(xs, since, signal),
    onTransition: (delta) =>
      applyClaimDelta(delta, storeClaims, updateStore, updateSnackBar),
    onNoChanges: () =>
      updateSnackBar({
        severity: "info",
        message: `Still posting ${ids.length} claim(s)...`,
      }),
  });
}

export function useInsuranceVerificationsPostingPoll(
  verifications: InsuranceVerificationMessage[],
  queryClient: QueryClient,
  updateSnackBar: Snackbar,
): void {
  const rqClient = useTanStackQueryClient();
  const ids = useMemo(
    () =>
      verifications
        .filter(insuranceVerificationNeedsPolling)
        .map((v) => v.wieldyId),
    [verifications],
  );
  usePostingStatusPoll({
    ids,
    fetchDelta: (xs, since, signal) =>
      queryClient.fetchIVPostingStatus(xs, since, signal),
    onTransition: (delta) =>
      applyVerificationDelta(delta, verifications, rqClient, updateSnackBar),
    onNoChanges: () =>
      updateSnackBar({
        severity: "info",
        message: `Still posting ${ids.length} insurance verification(s)...`,
      }),
  });
}
